import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useSEO from '../hooks/useSEO';
import AuthCarousel from '../components/AuthCarousel';
import AuthForm from '../components/AuthForm';

// Customer sign-in. The admin one-time-password flow that used to live here is gone — the
// website no longer has an admin area at all.
//
// Light by design: the rest of the site is dark and heritage-toned, so a white page here reads
// as a clean, deliberate moment rather than another dim panel. Gold carries the identity.

const GOLD = '#C9A558';
const GOLD_DEEP = '#8B6914';

export default function LoginPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useSEO({
    title: 'Sign In — Mama Africa Official',
    description: 'Sign in or create your Mama Africa account.',
  });

  // Home, not the account page. Signing in is rarely an errand in itself — people sign in to
  // carry on using the site, and landing on a settings screen is a dead end. The one exception
  // is being bounced here from somewhere specific (checkout, a gated page): that destination is
  // honoured, unless it was the account page itself.
  const cameFrom = location.state?.from;
  const destination = cameFrom && cameFrom !== '/account' ? cameFrom : '/';

  useEffect(() => {
    if (user) navigate(destination, { replace: true });
  }, [user, destination, navigate]);

  return (
    // page-wrapper carries the 108px offset that clears the fixed strip and navbar. Dropping
    // it is what tucked the card under the header.
    <div
      className="page-wrapper ma-auth-page"
      style={{ background: '#FFFFFF', display: 'flex', flexDirection: 'column' }}
    >
      <style>{`
        /* Width lives in a class, not an inline style: global-spread-layout force-stretches
           any direct child of .page-wrapper carrying an inline max-width, which flattened
           this card across the whole viewport. */
        /* Full-bleed: the carousel and the form own the screen, and the only thing below
           them is a thin footer strip. 108px is the fixed strip + navbar above. */
        .ma-auth-page { padding-bottom: 0 !important; }
        .ma-auth-card {
          flex: 1;
          width: 100%;
          display: grid;
          /* The carousel takes the larger share — it is what gives the page its warmth —
             while the form keeps a comfortable reading width rather than stretching. */
          grid-template-columns: minmax(0, 1.5fr) minmax(400px, 0.8fr);
          background: #fff;
          min-height: calc(100vh - 108px - 84px);
        }
        .ma-auth-carousel {
          position: relative;
          overflow: hidden;
          background: #1C1410;
        }
        .ma-auth-form {
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        .ma-auth-footer {
          width: 100%;
          padding: 18px 24px;
          border-top: 1px solid rgba(201,165,88,0.28);
          background: #FFFDF9;
          text-align: center;
          display: flex;
          flex-direction: column;
          gap: 6px;
          align-items: center;
          justify-content: center;
        }
        .ma-auth-footer a:hover { color: ${GOLD} !important; }
        @media (max-width: 1000px) {
          .ma-auth-card { grid-template-columns: 1fr; min-height: 0; }
          /* Kept on narrow screens, just shorter — losing it entirely strips the page of
             everything that made it feel like Mama Africa. */
          .ma-auth-carousel { min-height: 260px; }
          .ma-auth-form { padding: 40px 26px !important; }
        }
        @media (max-width: 520px) {
          .ma-auth-carousel { min-height: 190px; }
        }
      `}</style>

      <div className="ma-auth-card">
        <AuthCarousel />

        <div className="ma-auth-form" style={{ padding: '48px 44px' }}>
          <AuthForm onModeChange={() => {
            // Sign-up is taller than sign-in. Without this the page keeps the old scroll offset
            // and the new heading ends up behind the fixed navbar.
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }} />
        </div>
      </div>

      <AuthFooter />
    </div>
  );
}

/**
 * Footer for the sign-in page only. The site's main footer is dark and heavy; on a white page
 * it would fight the card, so this is a lighter version carrying the same essentials.
 */
function AuthFooter() {
  const year = new Date().getFullYear();
  return (
    <footer className="ma-auth-footer">
      {/* Deliberately two lines: the screen belongs to the carousel and the form, and a tall
          footer would take space from both for information nobody came here to read. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', alignItems: 'baseline', justifyContent: 'center' }}>
        <span style={{ fontFamily: "'Cinzel', serif", fontSize: 12, letterSpacing: '0.18em', color: GOLD_DEEP }}>
          MAMA AFRICA OFFICIAL
        </span>
        <span style={{ color: 'rgba(139,105,20,0.35)' }}>&middot;</span>
        <span style={{ fontFamily: "'EB Garamond', serif", fontSize: 14.5, fontStyle: 'italic', color: '#6B5B4A' }}>
          Heritage names, culture &amp; keepsakes for every generation
        </span>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 18px', justifyContent: 'center', alignItems: 'baseline' }}>
        <a href="mailto:Mamaafricaafia@gmail.com" style={footerLink}>Mamaafricaafia@gmail.com</a>
        <a href="/privacy-policy" style={footerLink}>Privacy</a>
        <a href="/terms" style={footerLink}>Terms</a>
        <a href="/shipping-returns" style={footerLink}>Shipping &amp; Returns</a>
        <span style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 11.5, color: '#9A8B7A' }}>
          &copy; {year} Ghana
        </span>
      </div>
    </footer>
  );
}

const footerLink = {
  fontFamily: "'Montserrat', sans-serif",
  fontSize: 13,
  color: GOLD_DEEP,
  textDecoration: 'none',
  borderBottom: '1px solid rgba(201,165,88,0.45)',
  paddingBottom: 2,
};
